import React from 'react';
import { PiCheck, PiX } from 'react-icons/pi';
import Loading from '../../loading';
import type {
  BaseProps,
  ComponentSize,
  ComponentVariant,
} from 'src/modules/shared/types/component.type';
import { getDisabledClasses } from 'src/modules/shared/styles/component.styles';
import { Text } from 'src/modules/shared/components/atoms/text';
import {
  triStateCheckboxBoxClasses,
  triStateCheckboxBoxIconClasses,
  triStateCheckboxIconClasses,
  triStateCheckboxLabelClasses,
  triStateCheckboxSizeClasses,
  triStateCheckboxStateClasses,
} from './styles';

interface TriStateCheckboxProps extends BaseProps {
  /** Current value: `true`, `false`, or `null` when it is not set. */
  value: boolean | null;
  /** Called with the next value in the cycle. */
  onChange: (value: boolean | null) => void;
  /** Accessible name; also the tooltip when there is no visible label. */
  ariaLabel: string;
  /** Visible label next to a checkbox-style box; the whole row then responds to clicks. */
  label?: React.ReactNode;
  /** Returns the tooltip for the value the next click sets; used with `label`. */
  getHint?: (nextValue: boolean | null) => string;
  /** Id of an element that explains the states, such as a legend. */
  ariaDescribedBy?: string;
  /** Values the control cycles through, in order. */
  cycleOrder?: readonly (boolean | null)[];
}

interface TriStateCheckboxIndicatorProps {
  /** Value whose box is drawn. */
  value: boolean | null;
  /** Box size. */
  size?: ComponentSize;
  /** Color variant for the yes state. */
  variant?: ComponentVariant;
}

const nextTriStateValue = (
  currentValue: boolean | null,
  cycleOrder: readonly (boolean | null)[]
): boolean | null => {
  const currentIndex = cycleOrder.findIndex((value) => value === currentValue);
  const nextIndex =
    currentIndex >= 0 ? (currentIndex + 1) % cycleOrder.length : 0;
  return cycleOrder[nextIndex] ?? null;
};

const getTriStateIcon = (
  value: boolean | null,
  className: string
): React.ReactNode => {
  if (value === true) {
    return <PiCheck className={className} aria-hidden="true" />;
  }

  if (value === false) {
    return <PiX className={className} aria-hidden="true" />;
  }

  return null;
};

const getTriStateName = (
  value: boolean | null
): keyof typeof triStateCheckboxLabelClasses => {
  if (value === true) {
    return 'checked';
  }

  return value === false ? 'unchecked' : 'indeterminate';
};

/**
 * Static checkbox-style box for a tri-state value, for example in a legend
 * that explains what each state looks like.
 */
export const TriStateCheckboxIndicator: React.FC<
  TriStateCheckboxIndicatorProps
> = ({ value, size = 'sm', variant = 'primary' }) => (
  <span
    aria-hidden="true"
    data-state={getTriStateName(value)}
    className={triStateCheckboxBoxClasses(value, variant, size)}
  >
    {getTriStateIcon(value, triStateCheckboxBoxIconClasses[size])}
  </span>
);

/**
 * Tri-state checkbox that cycles through unset, true and false. Without a
 * `label` it renders a single icon button; with a `label` it renders a full
 * row with a checkbox-style box and the label. The optional next-state hint
 * is the row's tooltip, so it never covers the label in narrow columns.
 */
const TriStateCheckbox: React.FC<TriStateCheckboxProps> = ({
  value,
  onChange,
  ariaLabel,
  label,
  getHint,
  ariaDescribedBy,
  cycleOrder = [null, true, false],
  size = 'md',
  variant = 'primary',
  appearance = 'filled',
  disabled = false,
  loading = false,
  className = '',
}) => {
  const isDisabled = disabled || loading;
  const nextValue = nextTriStateValue(value, cycleOrder);
  const state = getTriStateName(value);
  const sharedProps = {
    type: 'button' as const,
    role: 'checkbox',
    'aria-label': ariaLabel,
    'aria-checked': value ?? ('mixed' as const),
    'aria-describedby': ariaDescribedBy,
    'data-state': state,
    'data-appearance': appearance,
    'data-variant': variant,
    'data-size': size,
    disabled: isDisabled,
    onClick: () => onChange(nextValue),
  };

  if (label === undefined) {
    const iconClasses = triStateCheckboxIconClasses[size];

    return (
      <button
        {...sharedProps}
        title={ariaLabel}
        className={[
          'inline-flex items-center justify-center border transition-all duration-200 hover:shadow-sm',
          triStateCheckboxSizeClasses[size].button,
          triStateCheckboxStateClasses(value, variant, appearance),
          getDisabledClasses(isDisabled),
          className,
        ].join(' ')}
      >
        {loading ? (
          <Loading className={iconClasses} />
        ) : (
          getTriStateIcon(value, iconClasses)
        )}
      </button>
    );
  }

  return (
    <button
      {...sharedProps}
      title={getHint?.(nextValue)}
      className={[
        'group flex min-h-[44px] w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors duration-200 hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 md:min-h-[36px]',
        getDisabledClasses(isDisabled),
        className,
      ].join(' ')}
    >
      {loading ? (
        <Loading className={triStateCheckboxBoxIconClasses[size]} />
      ) : (
        <TriStateCheckboxIndicator
          value={value}
          size={size}
          variant={variant}
        />
      )}
      <Text
        as="span"
        size="small"
        weight="medium"
        className={triStateCheckboxLabelClasses[state]}
      >
        {label}
      </Text>
    </button>
  );
};

export default TriStateCheckbox;
